import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { fmtCurrency } from "@/utils/format";
import { History, X } from "lucide-react";

export default function ProductDetailDialog({ product, open, onOpenChange, canSeePrice = true }) {
  const [customer, setCustomer] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [rows, setRows] = useState([]);
  const [totalQty, setTotalQty] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [totalQuotes, setTotalQuotes] = useState(0);
  const [convertedQuotes, setConvertedQuotes] = useState(0);
  const [conversionRate, setConversionRate] = useState(0);
  const [loading, setLoading] = useState(false);

  const applyData = (data) => {
    setRows(data.items || []);
    setTotalQty(data.total_qty || 0);
    setTotalAmount(data.total_amount || 0);
    setTotalQuotes(data.total_quotes || 0);
    setConvertedQuotes(data.converted_quotes || 0);
    setConversionRate(data.conversion_rate || 0);
  };

  const load = async () => {
    if (!product?.id) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (customer.trim()) params.set("customer", customer.trim());
      if (startDate) params.set("start_date", startDate);
      if (endDate) params.set("end_date", endDate);
      const qs = params.toString() ? `?${params.toString()}` : "";
      const { data } = await api.get(`/products/${product.id}/sales-history${qs}`);
      applyData(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Satış geçmişi yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && product?.id) {
      // Modal her açılışta eski filtreleri sıfırla ve veriyi yükle
      setCustomer("");
      setStartDate("");
      setEndDate("");
      // İlk yüklemede filtresiz çağır
      (async () => {
        setLoading(true);
        try {
          const { data } = await api.get(`/products/${product.id}/sales-history`);
          applyData(data);
        } catch (e) {
          toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Satış geçmişi yüklenemedi");
        } finally {
          setLoading(false);
        }
      })();
    }
    // eslint-disable-next-line
  }, [open, product?.id]);

  const resetFilters = () => {
    setCustomer("");
    setStartDate("");
    setEndDate("");
    // Reset ettikten sonra tüm veriyi tekrar yükle
    setTimeout(load, 0);
  };

  const filteredCount = rows.length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none border-2 border-[#09090B] bg-white max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="product-detail-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl tracking-tight flex items-center gap-2">
            <History size={22} />
            {product?.name || "Ürün"}
            <span className="text-sm font-mono text-neutral-500 ml-2">· {product?.sku}</span>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <div className="border-2 border-[#09090B] p-3" data-testid="product-conversion-total-quotes">
              <div className="text-[10px] font-mono uppercase text-neutral-500">Toplam Teklif</div>
              <div className="text-2xl font-mono font-bold">{totalQuotes}</div>
            </div>
            <div className="border-2 border-[#09090B] p-3" data-testid="product-conversion-converted">
              <div className="text-[10px] font-mono uppercase text-neutral-500">Siparişe Dönüşen</div>
              <div className="text-2xl font-mono font-bold">{convertedQuotes}</div>
            </div>
            <div className="border-2 border-[#09090B] p-3 bg-[#09090B] text-white" data-testid="product-conversion-rate">
              <div className="text-[10px] font-mono uppercase text-neutral-300">Dönüşüm Oranı</div>
              <div className="text-2xl font-mono font-bold">%{conversionRate}</div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
            <div>
              <Label className="text-xs font-mono uppercase">Müşteri</Label>
              <Input
                value={customer}
                onChange={(e) => setCustomer(e.target.value)}
                placeholder="Firma adı ara…"
                className="rounded-none border-2 border-[#09090B]"
                data-testid="product-history-customer-filter"
              />
            </div>
            <div>
              <Label className="text-xs font-mono uppercase">Başlangıç</Label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="rounded-none border-2 border-[#09090B] font-mono"
                data-testid="product-history-start-date"
              />
            </div>
            <div>
              <Label className="text-xs font-mono uppercase">Bitiş</Label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="rounded-none border-2 border-[#09090B] font-mono"
                data-testid="product-history-end-date"
              />
            </div>
            <div className="flex gap-2">
              <Button
                className="rounded-none bg-[#09090B] hover:bg-[#0055FF] flex-1"
                onClick={load}
                disabled={loading}
                data-testid="product-history-apply-btn"
              >
                Filtrele
              </Button>
              <Button
                variant="outline"
                className="rounded-none border-2 border-[#09090B]"
                onClick={resetFilters}
                data-testid="product-history-reset-btn"
                title="Filtreleri temizle"
              >
                <X size={14} />
              </Button>
            </div>
          </div>

          <div className="flex items-center gap-4 text-xs font-mono text-neutral-700 border-t-2 border-b-2 border-[#09090B] py-2 px-1">
            <span data-testid="product-history-count">{filteredCount} satır</span>
            <span>·</span>
            <span data-testid="product-history-total-qty">Toplam Miktar: {totalQty} {product?.unit || ""}</span>
            {canSeePrice && (
              <>
                <span>·</span>
                <span data-testid="product-history-total-amount">Toplam Tutar: {fmtCurrency(totalAmount)}</span>
              </>
            )}
          </div>

          <div className="bg-white border-2 border-[#09090B]">
            <Table>
              <TableHeader>
                <TableRow className="bg-neutral-100 border-b-2 border-[#09090B]">
                  <TableHead className="font-mono uppercase text-xs">Tarih</TableHead>
                  <TableHead className="font-mono uppercase text-xs">Tür</TableHead>
                  <TableHead className="font-mono uppercase text-xs">Belge No</TableHead>
                  <TableHead className="font-mono uppercase text-xs">Müşteri</TableHead>
                  <TableHead className="font-mono uppercase text-xs text-right">Miktar</TableHead>
                  {canSeePrice && <TableHead className="font-mono uppercase text-xs text-right">Birim Fiyat</TableHead>}
                  {canSeePrice && <TableHead className="font-mono uppercase text-xs text-right">Satır Toplamı</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading && (
                  <TableRow>
                    <TableCell colSpan={canSeePrice ? 7 : 5} className="text-center py-6 text-neutral-500">Yükleniyor…</TableCell>
                  </TableRow>
                )}
                {!loading && rows.map((r, i) => (
                  <TableRow key={`${r.kind}-${r.ref_id}-${i}`} data-testid={`product-history-row-${i}`}>
                    <TableCell className="font-mono text-sm">{r.date}</TableCell>
                    <TableCell>
                      <span
                        className={`inline-block px-2 py-0.5 text-[10px] font-mono uppercase border-2 border-[#09090B] ${r.kind === "order" ? "bg-[#09090B] text-white" : "bg-white text-[#09090B]"}`}
                        data-testid={`product-history-kind-${i}`}
                      >
                        {r.label}
                      </span>
                    </TableCell>
                    <TableCell className="font-mono text-sm">{r.ref_number}</TableCell>
                    <TableCell className="font-semibold">{r.customer_name}</TableCell>
                    <TableCell className="text-right font-mono">{r.quantity} {r.unit}</TableCell>
                    {canSeePrice && <TableCell className="text-right font-mono">{fmtCurrency(r.unit_price)}</TableCell>}
                    {canSeePrice && <TableCell className="text-right font-mono font-bold">{fmtCurrency(r.line_total)}</TableCell>}
                  </TableRow>
                ))}
                {!loading && rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={canSeePrice ? 7 : 5} className="text-center py-8 text-neutral-500" data-testid="product-history-empty">
                      Kayıt bulunamadı
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
